import apiClient from './apiClient';

class BookingService {
    async getAllBookings(params = {}) {
        try {
            const response = await apiClient.get('/bookings', { params });
            return {
                success: true,
                data: response.data.data,
                meta: response.data.meta,
                pagination: response.data.meta // For backward compatibility
            };
        } catch (error) {
            console.error('Error fetching bookings:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch bookings'
            };
        }
    }

    /**
     * Download the filtered booking list as CSV.
     * Streamed from the server with no pagination, so every matching row is included.
     */
    async exportBookingsCsv(params = {}) {
        try {
            const response = await apiClient.get('/bookings/export-csv', {
                params,
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '');
            link.setAttribute('download', `bookings_${stamp}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            return { success: true };
        } catch (error) {
            console.error('Error exporting bookings CSV:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to export bookings'
            };
        }
    }

    async getBookingById(bookingId) {
        try {
            const response = await apiClient.get(`/bookings/${bookingId}`);
            return {
                success: true,
                data: response.data.data
            };
        } catch (error) {
            console.error('Error fetching booking:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch booking'
            };
        }
    }

    async createBooking(bookingData) {
        try {
            const response = await apiClient.post('/bookings', bookingData);
            return {
                success: true,
                data: response.data.data,
                message: response.data.message
            };
        } catch (error) {
            console.error('Error creating booking:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to create booking',
                errors: error.response?.data?.errors
            };
        }
    }

    async updateBooking(bookingId, bookingData) {
        try {
            const response = await apiClient.put(`/bookings/${bookingId}`, bookingData);
            return {
                success: true,
                data: response.data.data,
                message: response.data.message
            };
        } catch (error) {
            console.error('Error updating booking:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to update booking',
                errors: error.response?.data?.errors
            };
        }
    }

    async deleteBooking(bookingId) {
        try {
            const response = await apiClient.delete(`/bookings/${bookingId}`);
            return {
                success: true,
                message: response.data.message
            };
        } catch (error) {
            console.error('Error deleting booking:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to delete booking'
            };
        }
    }

    // Booking helper methods
    async generateAwbNumber() {
        try {
            const response = await apiClient.get('/booking-data/generate-awb');
            return {
                success: true,
                data: response.data.data.awb_no
            };
        } catch (error) {
            console.error('Error generating AWB:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to generate AWB number'
            };
        }
    }

    async getCities() {
        try {
            const response = await apiClient.get('/booking-data/cities');
            return {
                success: true,
                data: response.data.data
            };
        } catch (error) {
            console.error('Error fetching cities:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch cities'
            };
        }
    }

    // customerId narrows the list to the packages assigned to that customer.
    // Customers with no packages assigned are unrestricted, so omitting it (or
    // passing null for a walk-in booking) returns everything priced for the route.
    async getProductsForRoute(fromCityId, toCityId, customerId = null) {
        try {
            const response = await apiClient.get('/booking-data/products-for-route', {
                params: {
                    from_city_id: fromCityId,
                    to_city_id: toCityId,
                    ...(customerId ? { customer_id: customerId } : {})
                }
            });
            return {
                success: true,
                data: response.data.data
            };
        } catch (error) {
            console.error('Error fetching products for route:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch products for route'
            };
        }
    }

    async calculateRate(rateParams) {
        try {
            const response = await apiClient.post('/booking-data/calculate-rate', rateParams);
            return {
                success: true,
                data: response.data.data
            };
        } catch (error) {
            console.error('Error calculating rate:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to calculate rate'
            };
        }
    }

    async searchCustomers(searchTerm) {
        try {
            const response = await apiClient.get('/booking-data/search-customers', {
                params: { search: searchTerm }
            });
            return {
                success: true,
                data: response.data.data
            };
        } catch (error) {
            console.error('Error searching customers:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to search customers'
            };
        }
    }

    async getCustomerDetails(customerId) {
        try {
            const response = await apiClient.get(`/booking-data/customer-details/${customerId}`);
            return {
                success: true,
                data: response.data.data
            };
        } catch (error) {
            console.error('Error fetching customer details:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch customer details'
            };
        }
    }

    async searchAddresses(searchTerm, type = 'both', limit = 10) {
        try {
            const response = await apiClient.get('/booking-data/search-addresses', {
                params: { search: searchTerm, type, limit }
            });
            return {
                success: true,
                data: response.data.data
            };
        } catch (error) {
            console.error('Error searching addresses:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to search addresses'
            };
        }
    }

    async getAddressDetails(addressId) {
        try {
            const response = await apiClient.get(`/booking-data/address-details/${addressId}`);
            return {
                success: true,
                data: response.data.data
            };
        } catch (error) {
            console.error('Error fetching address details:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch address details'
            };
        }
    }

    async generateLabels(bookingId) {
        try {
            const response = await apiClient.get(`/bookings/${bookingId}/labels`);
            return {
                success: true,
                data: response.data.data
            };
        } catch (error) {
            console.error('Error generating labels:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to generate labels'
            };
        }
    }

    async getBookingStats(filters = {}) {
        try {
            const params = {};
            
            // Add all filter parameters (same as booking list API)
            if (filters.search) params.search = filters.search;
            if (filters.fromDate) params.from_date = filters.fromDate;
            if (filters.toDate) params.to_date = filters.toDate;
            if (filters.customerId) params.customer_id = filters.customerId;
            if (filters.status) params.status = filters.status;
            
            const response = await apiClient.get('/booking-data/stats', { params });
            return {
                success: true,
                data: response.data.data
            };
        } catch (error) {
            console.error('Error fetching booking stats:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch booking statistics'
            };
        }
    }

    async getAvailableStatuses() {
        try {
            const response = await apiClient.get('/booking-data/available-statuses');
            return {
                success: true,
                data: response.data.data
            };
        } catch (error) {
            console.error('Error fetching available statuses:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch available statuses'
            };
        }
    }

    async trackShipment(awbNumber) {
        try {
            const response = await apiClient.post('/track-shipment', {
                awb_no: awbNumber
            });
            return {
                success: true,
                data: response.data.data
            };
        } catch (error) {
            console.error('Error tracking shipment:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to track shipment'
            };
        }
    }

    // Utility methods
    validateBookingData(bookingData) {
        const errors = {};

        // General Details validation
        if (!bookingData.awb_no) {
            errors.awb_no = 'AWB number is required';
        }

        if (!bookingData.booking_date) {
            errors.booking_date = 'Booking date is required';
        }

        if (!bookingData.booking_time) {
            errors.booking_time = 'Booking time is required';
        }

        if (!bookingData.TXT_B_OFF_ID && bookingData.TXT_B_OFF_ID !== 0) {
            errors.TXT_B_OFF_ID = 'Booking office is required';
        }

        if (!bookingData.TXT_ORI_ID) {
            errors.TXT_ORI_ID = 'Origin city is required';
        }

        if (!bookingData.TXT_DES_ID) {
            errors.TXT_DES_ID = 'Destination city is required';
        }

        if (!bookingData.transport) {
            errors.transport = 'Shipment type is required';
        }

        if (!bookingData.payment_mode) {
            errors.payment_mode = 'Transaction mode is required';
        }

        // Shipper validation
        if (!bookingData.frm_name) {
            errors.frm_name = 'Shipper name is required';
        }

        if (!bookingData.frm_company) {
            errors.frm_company = 'Shipper company/division is required';
        }

        if (!bookingData.frm_address) {
            errors.frm_address = 'Shipper address is required';
        }

        if (!bookingData.frm_pincode) {
            errors.frm_pincode = 'Shipper pincode is required';
        } else if (!/^\d{6}$/.test(bookingData.frm_pincode)) {
            errors.frm_pincode = 'Shipper pincode must be 6 digits';
        }

        if (!bookingData.frm_tel) {
            errors.frm_tel = 'Shipper contact number is required';
        }

        // Recipient validation
        if (!bookingData.to_name) {
            errors.to_name = 'Recipient name is required';
        }

        if (!bookingData.to_company) {
            errors.to_company = 'Recipient company/division is required';
        }

        if (!bookingData.to_address) {
            errors.to_address = 'Recipient address is required';
        }

        if (!bookingData.to_pincode) {
            errors.to_pincode = 'Recipient pincode is required';
        } else if (!/^\d{6}$/.test(bookingData.to_pincode)) {
            errors.to_pincode = 'Recipient pincode must be 6 digits';
        }

        if (!bookingData.to_tel) {
            errors.to_tel = 'Recipient contact number is required';
        }

        // Billing validation
        if (!bookingData.no_of_pack || bookingData.no_of_pack < 1) {
            errors.no_of_pack = 'Number of packages must be at least 1';
        }

        // Package description validation
        if (!bookingData.package_name || bookingData.package_name.trim() === '') {
            errors.package_name = 'Package description is required';
        } else if (bookingData.package_name.trim().length < 3) {
            errors.package_name = 'Package description must be at least 3 characters long';
        } else if (bookingData.package_name.trim().length > 200) {
            errors.package_name = 'Package description cannot exceed 200 characters';
        } else if (!/^[a-zA-Z0-9\s\-_.,()&]+$/.test(bookingData.package_name.trim())) {
            errors.package_name = 'Package description can only contain letters, numbers, spaces, hyphens, underscores, commas, periods, parentheses, and ampersands';
        }

        if (!bookingData.bill_by) {
            errors.bill_by = 'Billing method is required';
        }

        if (bookingData.bill_by === 'Weight' && (!bookingData.act_wt || bookingData.act_wt <= 0)) {
            errors.act_wt = 'Actual weight is required for weight-based billing';
        }

        if (bookingData.bill_by === 'Dimention') {
            if (!bookingData.act_wt || bookingData.act_wt <= 0) {
                errors.act_wt = 'Actual weight is required';
            }
        }

        if (!bookingData.product_id) {
            errors.product_id = 'Product selection is required';
        }

        if (!bookingData.dec_value && bookingData.dec_value !== 0) {
            errors.dec_value = 'Declared value is required';
        }

        // Additional charges validation
        if (bookingData.remote_area_charges && parseFloat(bookingData.remote_area_charges) < 0) {
            errors.remote_area_charges = 'Remote area service charges cannot be negative';
        }

        return {
            isValid: Object.keys(errors).length === 0,
            errors
        };
    }

    calculateDimensionalWeight(length, width, height, transport = 'Surface') {
        if (!length || !width || !height) return 0;

        // Transport-specific dimensional weight calculation
        let divisor;
        if (transport === 'Air') {
            divisor = 6000;
        } else if (transport === 'Surface' || transport === 'Land') {
            divisor = 5000;
        } else {
            // Default to Surface calculation for unknown transport types
            divisor = 5000;
        }

        return Math.round((length * width * height) / divisor);
    }

    /**
     * Calculate volumetric weight for multiple boxes.
     * Returns per-box weights and totals.
     */
    calculateMultiBoxWeight(boxes, transport = 'Surface') {
        let divisor;
        if (transport === 'Air') {
            divisor = 6000;
        } else {
            divisor = 5000;
        }

        const boxResults = boxes.map((box, index) => {
            const l = parseFloat(box.length) || 0;
            const w = parseFloat(box.width) || 0;
            const h = parseFloat(box.height) || 0;
            const volWeight = (l > 0 && w > 0 && h > 0)
                ? Math.round(((l * w * h) / divisor) * 100) / 100
                : 0;
            return {
                ...box,
                box_number: index + 1,
                volumetric_weight: volWeight,
            };
        });

        const totalVolumetricWeight = boxResults.reduce(
            (sum, box) => sum + box.volumetric_weight, 0
        );

        return {
            boxes: boxResults,
            totalVolumetricWeight: Math.round(totalVolumetricWeight * 100) / 100,
        };
    }

    calculateGST(amount, rate = 18) {
        return Math.round((amount * rate / 100) * 100) / 100;
    }

    // Helper function to round amounts to 2 decimal places
    roundToTwoDecimals(amount) {
        return Math.round((parseFloat(amount) || 0) * 100) / 100;
    }

    formatCurrency(amount) {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(amount);
    }
}

export const bookingService = new BookingService(); 